import { useMemo } from 'react';
import styled from 'styled-components';

import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { StediPayer } from 'models/StediPayer';
import { indexStediPayers, stediPayerQueryKeys } from 'services/api/stediPayers';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import XIcon from 'svg/XIcon';

const MAX_STATES_LISTED = 5;

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

export function payerDetails({ operatingStates, coverageTypes, names }: StediPayer) {
  const states = operatingStates.length > MAX_STATES_LISTED ? `${operatingStates.length}` : operatingStates.join(', ');
  const others = names.length > 1 ? `${names.length}` : names[0];

  return [
    states && `States: ${states}`,
    coverageTypes.length > 0 && `Lines: ${coverageTypes.map(capitalize).join(', ')}`,
    others && `Other names: ${others}`,
  ]
    .filter(Boolean)
    .join(' • ');
}

interface Props {
  value: StediPayer[];
  onChange: (payers: StediPayer[]) => void;
  // Offers payers not yet at this facility; without one, those at none of the user's facilities.
  groupId?: string;
}

export default function StediPayerTypeahead({ value, onChange, groupId }: Props) {
  const params = { sortBy: 'search asc', ...(groupId ? { assignableTo: groupId } : { assignable: true }) };
  const directory = useInfiniteOptions({
    queryKey: stediPayerQueryKeys.index(params),
    fetchFn: indexStediPayers,
    params,
  });

  const picked = useMemo(() => new Set(value.map((payer) => payer.id)), [value]);

  return (
    <div>
      <FieldLabel>Search for payer</FieldLabel>
      <Select<StediPayer>
        {...directory}
        dataTestId='stedi-payer-typeahead'
        inputId='stedi-payer-typeahead'
        value={null}
        onChange={(payer) => payer && onChange([...value, payer])}
        getOptionValue={(payer) => payer.id}
        getOptionLabel={(payer) => payer.name}
        formatOptionLabel={(payer, meta) =>
          meta?.context === 'menu' ? (
            <>
              <div>{payer.name}</div>
              <Details>{[payerDetails(payer), picked.has(payer.id) && 'Selected'].filter(Boolean).join(' • ')}</Details>
            </>
          ) : (
            payer.name
          )
        }
        // Disabled rather than hidden: hiding every loaded option leaves no menu to scroll for the next page.
        isOptionDisabled={(payer: StediPayer) => picked.has(payer.id)}
        closeMenuOnSelect={false}
        noOptionsMessage={() => 'No matching payer found.'}
        placeholder='Start typing to search for a payer...'
        typeToSearchText='Start typing to search for a payer...'
      />
      {directory.options.isError && (
        <Unavailable data-testid='payer-search-unavailable'>
          Payer search is unavailable right now. Try again in a moment.
        </Unavailable>
      )}
      <Picked>
        <BodySmall>{`Selected Payers (${value.length}):`}</BodySmall>
        <Chips>
          {value.map((payer) => (
            <Chip key={payer.id}>
              {payer.name}
              <RemoveChip
                type='button'
                aria-label={`Remove ${payer.name}`}
                onClick={() => onChange(value.filter(({ id }) => id !== payer.id))}>
                <XIcon width={8} height={8} />
              </RemoveChip>
            </Chip>
          ))}
        </Chips>
      </Picked>
    </div>
  );
}

const Details = styled.div`
  font-size: 12px;
  color: ${colors.black50};
`;

const Unavailable = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.black50};
`;

const Picked = styled.div`
  margin-top: 24px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

// Matches MultiSelectChips' chips (shared/form/InfiniteScrollDropdown).
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 3px;
  background: ${colors.black10};
  font-size: 10px;
  color: ${colors.black100};
`;

const RemoveChip = styled.button`
  display: flex;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;
